import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Users, type User } from '@/lib/api';
import UserAvatar from './UserAvatar';

interface MentionAutocompleteProps {
  query: string; // current text after the '@' (no leading @)
  onSelect: (username: string) => void;
  onDismiss: () => void;
}

const MAX_RESULTS = 5;
const DEBOUNCE_MS = 150;

/**
 * Floating list of user suggestions for an `@mention`. The parent handles
 * positioning (typically absolute below the composer textarea). Arrow keys
 * move the active row, Enter picks it, Escape dismisses the picker.
 */
export default function MentionAutocomplete({ query, onSelect, onDismiss }: MentionAutocompleteProps) {
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Debounce search requests so we don't fire on every keystroke. The
  // AbortController is used to cancel in-flight requests when the query
  // changes before the previous response lands.
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);

    const handle = window.setTimeout(() => {
      Users.search(q, controller.signal)
        .then((r) => {
          if (cancelled) return;
          setResults(r.users.slice(0, MAX_RESULTS));
          setActiveIndex(0);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(handle);
    };
  }, [query]);

  // Reset active index when the result set shrinks so we don't point at an
  // out-of-bounds row.
  useEffect(() => {
    if (activeIndex >= results.length) setActiveIndex(0);
  }, [results, activeIndex]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[activeIndex];
      if (pick) onSelect(pick.username);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onDismiss();
    }
  }

  const trimmed = query.trim();
  const showEmpty = trimmed.length > 0 && !loading && results.length === 0;

  return (
    <div
      role="listbox"
      aria-label="Mention suggestions"
      onKeyDown={handleKeyDown}
      className={clsx(
        'z-30 mt-1 w-64 overflow-hidden rounded-lg border border-ink-100 bg-white shadow-soft',
        'dark:border-ink-700 dark:bg-ink-800',
      )}
    >
      <ul
        ref={listRef}
        className="nice-scroll max-h-60 overflow-y-auto py-1"
        role="presentation"
      >
        {loading && results.length === 0 && (
          <li className="px-3 py-2 text-xs text-ink-500 dark:text-ink-300">Searching…</li>
        )}
        {showEmpty && (
          <li className="px-3 py-2 text-xs text-ink-500 dark:text-ink-300">(no users match)</li>
        )}
        {results.map((u, idx) => (
          <li key={u.id} role="option" aria-selected={idx === activeIndex}>
            <button
              type="button"
              onClick={() => onSelect(u.username)}
              onMouseEnter={() => setActiveIndex(idx)}
              className={clsx(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                idx === activeIndex
                  ? 'bg-ink-100 dark:bg-ink-700'
                  : 'hover:bg-ink-50 dark:hover:bg-ink-700/60',
                'focus:outline-none focus-visible:bg-ink-100 dark:focus-visible:bg-ink-700',
              )}
            >
              <UserAvatar name={u.displayName || u.username} src={u.avatarUrl} size={28} />
              <span className="min-w-0 flex-1 truncate">
                <span className="block truncate font-medium">{u.displayName || u.username}</span>
                <span className="block truncate text-xs text-brand-600 dark:text-brand-300">@{u.username}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}